import { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { ChevronRight, X, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { TourStep } from './onboardingSteps';

interface OnboardingTourProps {
  steps: TourStep[];
  onComplete: () => void;
}

export function OnboardingTour({ steps, onComplete }: OnboardingTourProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [visible, setVisible] = useState(false);
  const tooltipRef = useRef<HTMLDivElement>(null);

  const step = steps[currentStep];

  const updateTargetRect = useCallback(() => {
    if (!step) return;
    const el = document.querySelector(step.targetSelector);
    if (el) {
      setTargetRect(el.getBoundingClientRect());
    } else {
      setTargetRect(null);
    }
  }, [step]);

  useEffect(() => {
    updateTargetRect();
    const timer = setTimeout(() => setVisible(true), 100);
    window.addEventListener('resize', updateTargetRect);
    window.addEventListener('scroll', updateTargetRect, true);
    return () => {
      window.removeEventListener('resize', updateTargetRect);
      window.removeEventListener('scroll', updateTargetRect, true);
      clearTimeout(timer);
    };
  }, [updateTargetRect, currentStep]);

  useEffect(() => {
    setExpanded(false);
  }, [currentStep]);

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      onComplete();
    }
  };

  const padding = 8;
  const overlayStyle = targetRect
    ? {
        clipPath: `polygon(
          0% 0%, 0% 100%, 
          ${targetRect.left - padding}px 100%, 
          ${targetRect.left - padding}px ${targetRect.top - padding}px, 
          ${targetRect.right + padding}px ${targetRect.top - padding}px, 
          ${targetRect.right + padding}px ${targetRect.bottom + padding}px, 
          ${targetRect.left - padding}px ${targetRect.bottom + padding}px, 
          ${targetRect.left - padding}px 100%, 
          100% 100%, 100% 0%
        )`,
      }
    : {};

  // Tooltip positioning
  const getTooltipStyle = (): React.CSSProperties => {
    if (!targetRect) return { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' };
    
    const tooltipWidth = 340;
    const gap = 12;
    let top = targetRect.bottom + gap;
    let left = targetRect.left + targetRect.width / 2 - tooltipWidth / 2;

    // Clamp horizontal
    left = Math.max(12, Math.min(left, window.innerWidth - tooltipWidth - 12));

    // If below viewport, place above
    if (top + 200 > window.innerHeight) {
      top = targetRect.top - gap - 200;
    }

    return { top, left, width: tooltipWidth };
  };

  return createPortal(
    <div
      className={`fixed inset-0 z-[9999] transition-opacity duration-300 ${visible ? 'opacity-100' : 'opacity-0'}`}
    >
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-black/60 transition-all duration-300"
        style={overlayStyle}
        onClick={onComplete}
      />

      {/* Tooltip */}
      <div
        ref={tooltipRef}
        className="absolute bg-popover border border-border rounded-xl shadow-2xl p-5 z-[10000] transition-all duration-300"
        style={getTooltipStyle()}
      >
        {/* Step indicator */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex gap-1.5">
            {steps.map((_, i) => (
              <div
                key={i}
                className={`h-1.5 rounded-full transition-all ${
                  i === currentStep ? 'w-6 bg-primary' : i < currentStep ? 'w-3 bg-primary/40' : 'w-3 bg-muted'
                }`}
              />
            ))}
          </div>
          <button
            onClick={onComplete}
            className="text-muted-foreground hover:text-foreground transition-colors p-1"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <h3 className="font-semibold text-foreground text-base mb-1">{step.title}</h3>
        <p className="text-sm text-muted-foreground leading-relaxed">{step.description}</p>

        {/* Expandable "Saber más" */}
        {step.expandedDescription && (
          <div className="mt-2">
            <button
              onClick={() => setExpanded(!expanded)}
              className="text-xs text-primary hover:text-primary/80 flex items-center gap-1 font-medium"
            >
              {expanded ? 'Menos detalle' : 'Quiero saber más'}
              {expanded ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
            </button>
            {expanded && (
              <p className="text-xs text-muted-foreground mt-2 leading-relaxed bg-muted/50 p-3 rounded-lg">
                {step.expandedDescription}
              </p>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-between mt-4">
          <button
            onClick={onComplete}
            className="text-xs text-muted-foreground hover:text-foreground transition-colors"
          >
            Saltar tour
          </button>
          <Button size="sm" onClick={handleNext} className="gap-1">
            {currentStep < steps.length - 1 ? (
              <>
                Siguiente
                <ChevronRight className="h-3.5 w-3.5" />
              </>
            ) : (
              '¡Entendido!'
            )}
          </Button>
        </div>
      </div>
    </div>,
    document.body
  );
}
